/**
 * useToolbarDrag — Draggable toolbar positioning hook.
 *
 * Encapsulates mouse-based drag-to-reposition with clamping to a container.
 * Position is null until the user drags, so the toolbar uses CSS default
 * positioning (centered at bottom). Once dragged, position is tracked as
 * absolute {x, y} relative to the container.
 *
 * Double-click the drag handle to reset to default position.
 */

import { useState, useCallback, useEffect, useRef } from "react";

/** Minimum margin from container edges */
const TOOLBAR_MARGIN = 12;

/** Selector for the visual island element inside the toolbar wrapper */
const ISLAND_SELECTOR = '[role="toolbar"]';

/** Get the bounding rect of the visual island, falling back to the toolbar wrapper */
function getIslandRect(toolbar: HTMLElement): DOMRect {
  const island = toolbar.querySelector<HTMLElement>(ISLAND_SELECTOR);
  return (island ?? toolbar).getBoundingClientRect();
}

/** Compute clamped x/y bounds within the container for the given island size */
function computeClampBounds(containerRect: DOMRect, islandSize: Pick<DOMRect, "width" | "height">) {
  const minX = TOOLBAR_MARGIN;
  const minY = TOOLBAR_MARGIN;
  return {
    minX,
    minY,
    maxX: Math.max(minX, containerRect.width - islandSize.width - TOOLBAR_MARGIN),
    maxY: Math.max(minY, containerRect.height - islandSize.height - TOOLBAR_MARGIN),
  };
}

/** Clamp a value between min and max */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export interface UseToolbarDragOptions {
  /** Ref to the container element that defines drag bounds */
  containerRef?: React.RefObject<HTMLElement | null> | undefined;
  /** Whether drag is enabled (default: true) */
  enabled?: boolean;
}

export interface UseToolbarDragResult {
  /** Ref to attach to the draggable element */
  toolbarRef: React.RefObject<HTMLDivElement>;
  /** Current position (null = default CSS positioning) */
  position: { x: number; y: number } | null;
  /** Whether the user is currently dragging */
  isDragging: boolean;
  /** Attach to the drag handle's onMouseDown */
  onDragHandleMouseDown: (e: React.MouseEvent) => void;
  /** Double-click handler to reset position to default */
  onDragHandleDoubleClick: () => void;
  /** Inline style to apply to the wrapper when position is set */
  dragStyle: React.CSSProperties;
}

export function useToolbarDrag({
  containerRef,
  enabled = true,
}: UseToolbarDragOptions = {}): UseToolbarDragResult {
  const toolbarRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  // null = default position (CSS centered at bottom), non-null = user has dragged
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  // Cache island dimensions at drag start to avoid querySelector on every mousemove
  const islandSizeRef = useRef({ width: 0, height: 0 });

  // Clamp position to container bounds on resize
  useEffect(() => {
    if (position === null || !enabled) return;

    const container = containerRef?.current;
    const toolbar = toolbarRef.current;
    if (!container || !toolbar) return;

    const clampToContainer = () => {
      const containerRect = container.getBoundingClientRect();
      const islandRect = getIslandRect(toolbar);
      if (containerRect.width === 0 || containerRect.height === 0) return;

      const bounds = computeClampBounds(containerRect, islandRect);

      setPosition((prev) => {
        if (prev === null) return null;
        const clampedX = clamp(prev.x, bounds.minX, bounds.maxX);
        const clampedY = clamp(prev.y, bounds.minY, bounds.maxY);
        if (clampedX === prev.x && clampedY === prev.y) return prev;
        return { x: clampedX, y: clampedY };
      });
    };

    const resizeObserver = new ResizeObserver(clampToContainer);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, position, enabled]);

  // Mouse down on drag handle — start tracking
  const onDragHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();

      const toolbar = toolbarRef.current;
      const container = containerRef?.current;
      if (!toolbar || !container) return;

      setIsDragging(true);

      const containerRect = container.getBoundingClientRect();
      // Use the island element's rect (not the wrapper's) for position calculation.
      // The wrapper spans full width (left:0, right:0) while the island is centered inside it.
      const islandRect = getIslandRect(toolbar);

      // Calculate current position relative to container
      const currentX = islandRect.left - containerRect.left;
      const currentY = islandRect.top - containerRect.top;

      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position?.x ?? currentX,
        posY: position?.y ?? currentY,
      };
      // Cache island size at drag start — avoids querySelector on every mousemove
      islandSizeRef.current = { width: islandRect.width, height: islandRect.height };
    },
    [enabled, position, containerRef]
  );

  // Mouse move + mouse up listeners (document-level while dragging)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef?.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      // Use cached island size from drag start (avoids querySelector per mousemove)
      const cachedSize = islandSizeRef.current;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const bounds = computeClampBounds(containerRect, cachedSize);
      const newX = clamp(dragStartRef.current.posX + deltaX, bounds.minX, bounds.maxX);
      const newY = clamp(dragStartRef.current.posY + deltaY, bounds.minY, bounds.maxY);

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, containerRef]);

  // Double-click to reset to default position
  const onDragHandleDoubleClick = useCallback(() => {
    setPosition(null);
  }, []);

  // Build inline style for the wrapper when user has dragged
  const dragStyle: React.CSSProperties = position
    ? {
        // Override CSS defaults: position absolutely within the container
        bottom: "auto",
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: "auto",
        // Keep centered alignment off when positioned absolutely
        alignItems: "flex-start",
      }
    : {};

  return {
    toolbarRef,
    position,
    isDragging,
    onDragHandleMouseDown,
    onDragHandleDoubleClick,
    dragStyle,
  };
}
